import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import { applyAiResult, selectionOffsets } from "~/features/documents/editor/applyAiResult";
import { useSelectionActionsStore } from "~/features/documents/editor/selectionActionsStore";
import type { SelectionAction } from "~/features/documents/editor/selectionTypes";

/**
 * Runs one selection action from click to accepted, for the markdown (v1) lane.
 *
 * The v2 lane does not come through here — `EditorV2` owns its own bubble bar and dispatches
 * results into the CRDT through the engine's single write path. This is the same flow for the
 * lane that saves through markdown, and it deliberately ports the LIVE kms path rather than
 * the unused hook beside it: the live path posts to the native `ai-actions/transform-selection`
 * and reads `transformedText`, while the dead one still spoke a retired TomatoFlow DTO.
 *
 * The one thing taken from that dead hook is its best idea: an `AbortController`, so an
 * abandoned refinement actually stops. The live path's `AbortError` guards could never fire
 * because nothing ever aborted (§F6.10).
 */
export function useSelectionActions(options: {
  editor: Editor | null;
  documentId: string;
  collectionId: string | null;
  documentTitle: string;
  /** False for a reader: the affordance is gated on the update ability (R9). */
  enabled: boolean;
}) {
  const { editor, documentId, collectionId, documentTitle, enabled } = options;
  const { t } = useTranslation();
  const api = useApi();
  const store = useSelectionActionsStore();
  const [busy, setBusy] = useState(false);
  const [lastLane, setLastLane] = useState<"act" | "propose" | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Whatever is in flight when this unmounts is abandoned, not left streaming into nothing.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  /**
   * The gate is checked ONCE per run, and it fails OPEN: only an explicit
   * `provider_not_configured` shows the wall. Anything else — an old backend's 404, a 401, a
   * transport failure — proceeds, because a false "configure your provider" in front of a
   * working AI is worse than no gate at all.
   */
  const checkGate = useCallback(async (): Promise<boolean> => {
    try {
      const status = await api.aiActions.providerStatus();
      if (!status.configured) {
        store.gate(t("An administrator can add one in AI settings."));
        return false;
      }
    } catch {
      // Fails open, by design.
    }
    return true;
  }, [api, store, t]);

  const run = useCallback(
    async (action: SelectionAction) => {
      if (!editor || !enabled || busy) {
        return;
      }
      const selection = selectionOffsets(editor);
      if (!selection || selection.text.trim().length === 0) {
        toast.error(t("Select some text first"));
        return;
      }

      // Viewport rect of the selection, for the drawer's anchor. Physical geometry, which is
      // why it is measured rather than described in classes.
      const rect = window.getSelection()?.rangeCount
        ? window.getSelection()?.getRangeAt(0).getBoundingClientRect()
        : null;

      store.open({
        action,
        range: { from: selection.from, to: selection.to },
        originalText: selection.text,
        bounds: rect
          ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right }
          : null,
      });

      if (!(await checkGate())) {
        return;
      }

      setBusy(true);
      try {
        const outcome = await api.selection.transform({
          documentId,
          ...(collectionId ? { collectionId } : {}),
          documentTitle,
          instructions: action.prompt,
          selectedText: selection.text,
        });

        if (outcome.kind === "dispatch") {
          // The resolved thinker is pinned to somebody's VS Code window: say so while polling,
          // rather than leaving a spinner that looks stuck.
          store.addThought(
            t("Queued in {{name}}…", { name: outcome.thinkerName ?? t("an editor window") }),
          );
          const text = await pollDispatch(api, outcome.dispatchId, (thought) =>
            store.addThought(thought),
          );
          if (!text?.trim()) {
            store.fail(t("The AI did not return a result."));
            return;
          }
          store.setResult(text);
          return;
        }

        if (!outcome.text.trim()) {
          store.fail(t("The AI did not return a result."));
          return;
        }
        if (outcome.thinkerName) {
          store.addThought(t("Answered by {{name}}", { name: outcome.thinkerName }));
        }
        store.setResult(outcome.text);
      } catch (error) {
        // The server's own reason, never a paraphrase: a named thinker timeout and a bad
        // request are different problems and the reader must be able to tell them apart.
        store.fail(error instanceof Error ? error.message : t("Something went wrong"));
      } finally {
        setBusy(false);
      }
    },
    [api, busy, checkGate, collectionId, documentId, documentTitle, editor, enabled, store, t],
  );

  const refine = useCallback(
    (message: string) => {
      const state = useSelectionActionsStore.getState();
      if (!message.trim() || busy) {
        return;
      }
      state.addTurn({ role: "user", content: message });
      state.beginStreaming();
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);

      void api.selection
        .refine(
          {
            originalText: state.originalText,
            currentResult: state.result,
            followUpMessage: message,
            conversationHistory: state.turns.map((turn) => ({
              role: turn.role === "user" ? "user" : "assistant",
              content: turn.content,
            })),
            documentId,
            ...(collectionId ? { collectionId } : {}),
          },
          (event) => useSelectionActionsStore.getState().applyEvent(event),
          controller.signal,
        )
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          useSelectionActionsStore
            .getState()
            .fail(error instanceof Error ? error.message : t("Something went wrong"));
        })
        .finally(() => setBusy(false));
    },
    [api, busy, collectionId, documentId, t],
  );

  /**
   * Accept applies through the CRDT-safe primitive, never a raw text replace — and only after
   * confirming the range still exists. A stale range surfaces a notice instead of quietly
   * dropping the result somewhere else in the document.
   */
  const accept = useCallback(
    (result: string): boolean => {
      const state = useSelectionActionsStore.getState();
      if (!editor || !state.range) {
        return false;
      }
      const applied = applyAiResult(editor, result, state.range);
      if (!applied) {
        toast.error(t("This result no longer fits the document."));
        return false;
      }
      state.close();
      editor.commands.focus();
      return true;
    },
    [editor, t],
  );

  const dismiss = useCallback(() => {
    abortRef.current?.abort();
    setBusy(false);
    useSelectionActionsStore.getState().close();
    editor?.commands.focus();
  }, [editor]);

  return { run, refine, accept, dismiss, busy, lastLane, setLastLane };
}

/** Polls an IDE-pinned dispatch until it settles, narrating the queue while it waits. */
async function pollDispatch(
  api: ReturnType<typeof useApi>,
  dispatchId: string,
  onProgress: (thought: string) => void,
): Promise<string | null> {
  const deadline = Date.now() + 10 * 60_000;
  let announced = -1;
  for (;;) {
    const dispatch = await api.selection.dispatchResult(dispatchId);
    if (api.selection.dispatchSettled(dispatch)) {
      if (dispatch.error) {
        throw new Error(dispatch.error);
      }
      return dispatch.transformedText;
    }
    if (dispatch.queuedBehind !== announced) {
      announced = dispatch.queuedBehind;
      onProgress(`${dispatch.statusDetail} · ${dispatch.queuedBehind}`);
    }
    if (Date.now() > deadline) {
      throw new Error("The editor window did not answer in time.");
    }
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
}
